import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import { photosApi, PHOTO_CATEGORIES } from '../services/photosApi';
import './Galerie.css';

// Tuiles du hero mosaic (background) — toujours statiques (juste des seeds)
const HERO_TILES = [
  { cls: 't1', seed: 'cb-gal-hero-drone' },
  { cls: 't2', seed: 'cb-gal-hero-procession' },
  { cls: 't3', seed: 'cb-gal-hero-pirogue' },
  { cls: 't4', seed: 'cb-gal-hero-portrait' },
  { cls: 't5', seed: 'cb-gal-hero-mosquee' },
  { cls: 't6', seed: 'cb-gal-hero-foot' },
  { cls: 't7', seed: 'cb-gal-hero-plage' },
  { cls: 't8', seed: 'cb-gal-hero-tieb' },
  { cls: 't9', seed: 'cb-gal-hero-marche' },
];

const CATEGORIES = PHOTO_CATEGORIES; // alias

// Photographe du mois
const PHOTOGRAPHER = {
  name: "Mor Sène",
  role: "Photographe résident · Sport, Drone & Vie quotidienne",
  bio: "Né à Cambérène en 1989, Mor capture le village depuis 2011. Spécialiste du sport et du drone, il signe les images de l'ASC depuis 5 saisons et tient les archives visuelles de la mairie.",
  stats: [
    { val: '142', lbl: 'Clichés' },
    { val: '5',   lbl: 'Saisons ASC' },
    { val: '2011', lbl: 'Depuis' },
  ],
  photos: [
    { cls: 'featured', seed: 'cb-mor-feat-1' },
    { cls: '',         seed: 'cb-mor-2' },
    { cls: '',         seed: 'cb-mor-3' },
    { cls: '',         seed: 'cb-mor-4' },
    { cls: '',         seed: 'cb-mor-5' },
  ],
};

// Collection thématique en vedette
const COLLECTION = {
  title: "Golden hour",
  subtitle: "16 photos · automne 2025",
  desc: "La côte de Cambérène entre 17h et 19h — quand la lumière passe du doré au pourpre. Une heure et demie qui change tout.",
  photos: [
    { seed: 'cb-col-gold-1', caption: "Plage Nord · 17h42" },
    { seed: 'cb-col-gold-2', caption: "Pirogues en retour · 18h08" },
    { seed: 'cb-col-gold-3', caption: "Reflet sur l'eau · 18h31" },
    { seed: 'cb-col-gold-4', caption: "Coucher sur la digue · 18h54" },
  ],
};

// Tous les photographes contributeurs
const PHOTOGRAPHERS = [
  { name: "Mor Sène",        role: "Sport · Drone",    seed: "cb-voi-mor" },
  { name: "Aïssatou N.",     role: "Plage · Aube",     seed: "cb-voi-aissatou" },
  { name: "Fatou Laye",      role: "Cérémonies",       seed: "cb-voi-fatou" },
  { name: "Cheikh Diop",     role: "Quotidien",        seed: "cb-voi-cheikh-d" },
  { name: "Yacine Ba",       role: "Portraits",        seed: "cb-voi-yacine" },
  { name: "Modou Sarr",      role: "Pêche · Mer",      seed: "cb-voi-modou" },
];

const Lieux = () => {
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('masonry'); // 'masonry' | 'uniform'
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.gal-page .reveal').forEach((el) => io.observe(el));
    applyLang(getCurrentLang());
    return () => io.disconnect();
  });

  // Fetch toutes les photos publiées une fois ; le filtre est client-side
  useEffect(() => {
    let cancelled = false;
    photosApi.list({ limit: 300 })
      .then((data) => { if (!cancelled) { setPhotos(data.items); setLoading(false); } })
      .catch(() => { if (!cancelled) { setPhotos([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // Compteurs live par catégorie (utilisés par les chips)
  const counts = useMemo(() => {
    const c = { all: photos.length };
    for (const p of photos) c[p.category] = (c[p.category] || 0) + 1;
    return c;
  }, [photos]);

  // Photos hors-collection pour la masonry principale (les "Golden hour" etc. ont leur propre section)
  const mainPhotos = useMemo(() => photos.filter((p) => !p.collection), [photos]);

  // Photos de la collection vedette (la plus représentée parmi les featured)
  const featuredCollection = useMemo(() => {
    const groups = new Map();
    for (const p of photos) {
      if (!p.collection) continue;
      groups.set(p.collection, (groups.get(p.collection) || 0) + 1);
    }
    if (groups.size === 0) return null;
    const [topName] = [...groups.entries()].sort((a, b) => b[1] - a[1])[0];
    const items = photos.filter((p) => p.collection === topName).slice(0, 4);
    return { name: topName, items };
  }, [photos]);

  const visiblePhotos = useMemo(() => {
    if (filter === 'all') return mainPhotos;
    return mainPhotos.filter((p) => p.category === filter);
  }, [filter, mainPhotos]);

  // Lightbox controls
  const openLightbox = useCallback((idx) => setLightboxIdx(idx), []);
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const nextPhoto = useCallback(() => {
    setLightboxIdx((i) => (i === null ? null : (i + 1) % visiblePhotos.length));
  }, [visiblePhotos.length]);
  const prevPhoto = useCallback(() => {
    setLightboxIdx((i) => (i === null ? null : (i - 1 + visiblePhotos.length) % visiblePhotos.length));
  }, [visiblePhotos.length]);

  // Keyboard control de la lightbox
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') nextPhoto();
      else if (e.key === 'ArrowLeft')  prevPhoto();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [lightboxIdx, closeLightbox, nextPhoto, prevPhoto]);

  const current = lightboxIdx !== null ? visiblePhotos[lightboxIdx] : null;

  return (
    <main className="gal-page">
      {/* ============== HERO MOSAIC ============== */}
      <header className="gal-hero">
        <div className="gal-hero-grid" aria-hidden="true">
          {HERO_TILES.map((t) => (
            <div
              key={t.cls}
              className={`gal-hero-tile ${t.cls}`}
              style={{ backgroundImage: `url('https://picsum.photos/seed/${t.seed}/1000/800')` }}
            />
          ))}
        </div>
        <div className="gal-hero-veil" />
        <div className="gal-hero-grain" />

        <div className="wrap gal-hero-inner reveal">
          <span className="gal-hero-eyebrow">Bibliothèque visuelle · 480+ clichés</span>
          <h1>
            Le village,<br />
            <span className="ital">en images.</span>
          </h1>
          <p className="lede">
            Drone, vie de plage, cérémonies layènes, foot, pêche, portraits — une bibliothèque
            visuelle ouverte à la communauté, contribuée et vérifiée par les habitants.
          </p>

          <div className="gal-hero-foot">
            <div><b>{loading ? '—' : photos.length}</b><span>Clichés publiés</span></div>
            <div><b>{new Set(photos.map((p) => p.author)).size || '—'}</b><span>Photographes</span></div>
            <div><b>{Object.keys(counts).filter((k) => k !== 'all' && counts[k] > 0).length}</b><span>Catégories actives</span></div>
            <div><b>2011</b><span>Plus ancienne archive</span></div>
          </div>
        </div>
      </header>

      {/* ============== FILTRES STICKY ============== */}
      <div className="gal-filters">
        <div className="wrap">
          <span className="lab">Filtrer —</span>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              className={`gal-chip${filter === c.id ? ' on' : ''}`}
              onClick={() => setFilter(c.id)}
            >
              {c.lbl}
              <span className="ct">{counts[c.id] || 0}</span>
            </button>
          ))}
          <span className="spacer" />
          <div className="view" aria-label="Vue">
            <button
              className={view === 'masonry' ? 'on' : ''}
              onClick={() => setView('masonry')}
              title="Mosaïque"
            >
              Mosaïque
            </button>
            <button
              className={view === 'uniform' ? 'on' : ''}
              onClick={() => setView('uniform')}
              title="Grille"
            >
              Grille
            </button>
          </div>
        </div>
      </div>

      {/* ============== MASONRY ============== */}
      <section className="gal-masonry-section">
        <div className="wrap">
          {loading && (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(250,246,238,.55)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>
              Chargement de la galerie…
            </p>
          )}

          {!loading && (
            <div className={`gal-masonry ${view === 'uniform' ? 'uniform' : ''}`}>
              {visiblePhotos.map((p, idx) => {
                const img = p.imageUrl || `https://picsum.photos/seed/${p.imageSeed || `cb-photo-${p.id}`}/1400/1000`;
                return (
                  <div
                    key={p.id}
                    className={`gal-tile ${p.size}`}
                    onClick={() => openLightbox(idx)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') openLightbox(idx); }}
                  >
                    <div className="img" style={{ backgroundImage: `url('${img}')` }} />
                    <div className="meta">
                      <span className="cat">{p.category}</span>
                      <p className="cap">{p.title}</p>
                      <span className="author">© {p.author}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && visiblePhotos.length === 0 && (
            <p style={{ textAlign: 'center', padding: '60px 0', color: 'rgba(250,246,238,.55)' }}>
              Aucune photo pour cette catégorie.
            </p>
          )}
        </div>
      </section>

      {/* ============== PHOTOGRAPHE DU MOIS ============== */}
      <section className="gal-photographer">
        <div className="wrap">
          <div className="pgh-shell">
            <div className="pgh-meta reveal">
              <span className="eyebrow">Photographe du mois</span>
              <h2>{PHOTOGRAPHER.name},<br /><span className="ital">l'œil du village.</span></h2>
              <p className="bio">{PHOTOGRAPHER.bio}</p>
              <div className="stats">
                {PHOTOGRAPHER.stats.map((s) => (
                  <div key={s.lbl}><b>{s.val}</b><span>{s.lbl}</span></div>
                ))}
              </div>
            </div>

            <div className="pgh-photos reveal">
              {PHOTOGRAPHER.photos.map((p, i) => (
                <div
                  key={i}
                  className={`ph ${p.cls}`}
                  style={{ backgroundImage: `url('https://picsum.photos/seed/${p.seed}/900/700')` }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== COLLECTION THÉMATIQUE ============== */}
      {featuredCollection && featuredCollection.items.length > 0 && (
      <section className="gal-collection">
        <div className="wrap">
          <div className="col-head reveal">
            <span className="eyebrow">Collection en vedette</span>
            <h2>« <span className="ital">{featuredCollection.name}</span> »</h2>
            <p>{COLLECTION.desc} <br /><strong style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--char-500)' }}>{featuredCollection.items.length} photos · sélection en vedette</strong></p>
          </div>

          <div className="col-strip">
            {featuredCollection.items.map((p) => (
              <div
                key={p.id}
                className="col-tile reveal"
                data-caption={p.title}
                style={{ backgroundImage: `url('${p.imageUrl || `https://picsum.photos/seed/${p.imageSeed || `cb-photo-${p.id}`}/900/1200`}')` }}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ============== TOUS LES PHOTOGRAPHES ============== */}
      <section className="gal-photographers">
        <div className="wrap">
          <div className="pgs-head reveal">
            <span className="eyebrow">Tous les contributeurs</span>
            <h2>Douze regards <span style={{ fontStyle: 'italic', color: 'var(--gold-soft)' }}>· un village.</span></h2>
          </div>
          <div className="pgs-grid">
            {PHOTOGRAPHERS.map((p) => (
              <div key={p.name} className="pg-card reveal">
                <div className="av" style={{ backgroundImage: `url('https://picsum.photos/seed/${p.seed}/200/200')` }} />
                <h5>{p.name}</h5>
                <small>{p.role}</small>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== CTA ============== */}
      <section className="gal-cta">
        <div className="wrap reveal">
          <span className="eyebrow">Contribuer</span>
          <h3>Vous avez une photo <span className="ital">de Cambérène ?</span></h3>
          <p>
            Aube, fête, sport, paysage, portrait — toute image qui dit le village a sa place ici.
            Soumettez vos clichés, ils seront vérifiés puis publiés.
          </p>
          <div className="gal-cta-actions">
            <a href="#" className="btn btn-primary">
              <span>Soumettre une photo</span>
              <span className="arrow">→</span>
            </a>
            <Link to="/bibliotheque" className="btn btn-ghost">
              <span>Archives bibliothèque</span>
              <span className="arrow">↗</span>
            </Link>
            <Link to="/actualites" className="btn btn-ghost">
              <span>Photo de la semaine</span>
              <span className="arrow">↗</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ============== LIGHTBOX ============== */}
      {current && (
        <div className="gal-lightbox" onClick={closeLightbox}>
          <button
            className="gal-lightbox-close"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            aria-label="Fermer"
          >
            ✕
          </button>
          <button
            className="gal-lightbox-nav prev"
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            aria-label="Photo précédente"
          >
            ‹
          </button>
          <button
            className="gal-lightbox-nav next"
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            aria-label="Photo suivante"
          >
            ›
          </button>
          <div
            className="gal-lightbox-img"
            style={{ backgroundImage: `url('${current.imageUrl || `https://picsum.photos/seed/${current.imageSeed || `cb-photo-${current.id}`}/1800/1300`}')` }}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="gal-lightbox-meta" onClick={(e) => e.stopPropagation()}>
            <p className="cap">« {current.title} »</p>
            <p className="author">© {current.author} · {current.category} · {lightboxIdx + 1} / {visiblePhotos.length}</p>
          </div>
        </div>
      )}
    </main>
  );
};

export default Lieux;
